import { useEffect } from 'react';
import { useCompanyUIConfig } from '_clean-code/api/hooks/useCompanyUIConfig';
import { Logger } from '_clean-code/utils/logger';
import { localStore, sessionStore } from '_clean-code/utils/storage';

export type Channel = 'canary' | 'stable';

/** Read by the CloudFront Function (FE-801) to pick which index.html to serve. */
export const CHANNEL_COOKIE = 'pulsar_channel';
const COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

/**
 * Staff/CS pick a build with `?channel=canary|stable`; `?channel=auto` goes back
 * to following the company flag. Persisted because the flag would otherwise undo
 * the manual choice on the very next load.
 */
export const CHANNEL_OVERRIDE_KEY = 'PULSAR#CHANNEL_OVERRIDE';

/**
 * The channel this tab last reloaded for. If the cookie still doesn't match
 * after that reload (cookies blocked, a webview dropping `Secure` cookies), a
 * second reload can't help, so we stop instead of looping — the same guard
 * RefreshPageChecker uses for versions.
 */
export const RELOADED_FOR_CHANNEL_KEY = 'PULSAR#RELOADED_FOR_CHANNEL';

const isChannel = (value: string | null): value is Channel =>
  value === 'canary' || value === 'stable';

export function readChannelCookie(): Channel {
  const isCanary = document.cookie
    .split(';')
    .some((cookie) => cookie.trim() === `${CHANNEL_COOKIE}=canary`);
  return isCanary ? 'canary' : 'stable';
}

function writeChannelCookie(channel: Channel) {
  document.cookie =
    channel === 'canary'
      ? `${CHANNEL_COOKIE}=canary; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Secure; SameSite=Lax`
      : `${CHANNEL_COOKIE}=; Path=/; Max-Age=0; Secure; SameSite=Lax`;
}

function readOverride(): Channel | null {
  const param = new URLSearchParams(window.location.search).get('channel');
  if (isChannel(param)) localStore.write(CHANNEL_OVERRIDE_KEY, param);
  else if (param === 'auto') localStore.remove(CHANNEL_OVERRIDE_KEY);

  const stored = localStore.read(CHANNEL_OVERRIDE_KEY);
  return isChannel(stored) ? stored : null;
}

/**
 * Keeps the `pulsar_channel` cookie in line with the company's `webCanary` flag
 * (or a manual `?channel=` override), reloading once when it changes so
 * CloudFront serves the matching build. Call it only once the selected company
 * is known, otherwise the config request goes out without `x-company-id`.
 */
export function useCanaryChannel() {
  const { data: config } = useCompanyUIConfig();
  // A failed request leaves `config` undefined, so we wait rather than treat
  // the flag as off and pull canary companies back to stable.
  const flagsLoaded = config?.featureFlags !== undefined;
  const webCanary = config?.featureFlags?.webCanary?.enabled === true;

  useEffect(() => {
    const override = readOverride();
    if (!override && !flagsLoaded) return;

    const desired: Channel = override ?? (webCanary ? 'canary' : 'stable');
    if (readChannelCookie() === desired) {
      sessionStore.remove(RELOADED_FOR_CHANNEL_KEY);
      return;
    }

    if (sessionStore.read(RELOADED_FOR_CHANNEL_KEY) === desired) {
      Logger.error(
        'useCanaryChannel',
        `Already reloaded for channel "${desired}" but the cookie still doesn't match; not reloading again`
      );
      return;
    }

    writeChannelCookie(desired);
    sessionStore.write(RELOADED_FOR_CHANNEL_KEY, desired);
    window.location.reload();
  }, [flagsLoaded, webCanary]);
}
